/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withPerfMarker } from '@bingads-webui/performance-marker-react-wrapper';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { Panel } from '@bingads-webui-react/primitive';
import { MediaEditorPanel } from '@bingads-webui-campaign-react/media-editor-panel';
import { PivotItemKey } from '@bingads-webui-campaign-react/media-editor';
import { MediaRepository } from '@bingads-webui-campaign/media-repository';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { odata } from '@bingads-webui/http-util';
import legacyOdata from '@bingads-webui-component-legacy/http/channel/odata';
import {
  PreviewCarouselRightRailWrapper,
  PreviewSingleRightRailWrapper,
} from '@bingads-webui-campaign-react/preview-right-rail-wrapper';
import { getFolderODataService, transformAltTextRequest } from '@bingads-webui-campaign-react/asset-library-services';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { isTestEnvironment } from '@bingads-webui-campaign/global-app-init';
import { AddImageCrudAdComponent, VIEWS } from '@bingads-webui-campaign-react/add-media-panel-crud-ad';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { StockImageDataService } from '@bingads-webui-campaign/stock-image-data-service';
import { InvestImageDataService } from '@bingads-webui-campaign/invest-image-data-service';
import { ResponsiveImageListView } from '@bingads-webui-campaign-react/responsive-image-list-view';
import { getAdCreatorInstrumentation } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { I18nModel } from '@bingads-webui-universal/i18n-model';
import { ImageExperienceVideo, VideoExperience } from '@bingads-webui-campaign-react/image-experience-video-part';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import { AddImageContainer } from './add-image';
import { EditImage } from './edit-image';
import { ImageListView } from './image-list-view';
import { ImageExperienceImage } from './model/image-experience-image';
import { CustomThumbnailRecordType } from './types';
import C from './constants';
import { getEditingMediaProps } from './util/editing-media-props';
import getAutoCroppedImages from './util/get-cropped-image';
import { ImageRepository } from './util/image-repository';
import {
  validateImageSizeAsync,
  validateImageWithDimension,
} from './util/image-size-validation';
import { getImageValidationErrorMessage, getMultiAddedImageErrorMessage } from './util/image-validation-handling';
import { MMAPreviewCarousel } from './image-preview/mma-msan-preview-carousel';
import { PreviewGeneral } from './image-preview/preview-general';
import { PreviewCarousel } from './image-preview/preview-carousel';
import StockImageVendor from './add-image/stock-image/stockImageVendor';
import SocialMediaRepository from './util/social-media-repository';
import SocialMediaDataSource from './add-image/data-source/social-media-data-source';
import { UnifiedAdPreviewSinglePlacementWrapper } from './ad-preview-library-wrapper/unified-ad-preview-single-placement-wrapper';
import { UnifiedAdPreviewCarouselLayoutWrapper } from './ad-preview-library-wrapper/unified-ad-preview-carousel-layout-wrapper';

const getComponent = getComponentAsync => () => getComponentAsync()
  .then(component => ({ default: component }));

const getChangeImageBackgroundDialogAsync = () => import(/* webpackMode: 'eager' */'@bingads-webui-campaign-react/copilot-change-image-background-dialog')
  .then(({ CopilotChangeImageBackgroundDialog }) => CopilotChangeImageBackgroundDialog);

export class ImageExperienceComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    const {
      i18n,
      isAudienceAd,
      isMultimediaAd,
      isMultimediaAdinMSANEnabled,
      cid,
      aid,
      oDataConfig,
      isAdCreatorEnabled,
      setAddNewImageCallback,
      setAddNewImagesCallback,
      setOpenAddImageCallback,
      customMaxImageCount,
      enableImageDeleting,
      enableStockMultipleSelection,
      enableInvestMultipleSelection,
      enableWebsiteMultipleSelection,
      instrumentation,
      mediaRepository,
      isImageAdExtensionMode,
      isCombinedMsanAndMmaAspectRatios,
      isMMAV2Enabled,
      adCreatorScenario,
      isMSANBoostEnabled,
      isBoost200x100ImageEnabled,
      isBoostNewAspectRatioSupportEnabled,
      permissions,
      isStoreAppProng,
      isStoreAppSpotLight,
      isStoreAppTransparentLogo,
      isUnifiedAppCampaignWindowsStore,
      isLinkedInAd,
      isImageFittingEnabled,
      isPMaxAspectRatiosV2Enabled,
    } = props;

    this.defaultPreviewFields = {
      shortHeadline: i18n.getString('Audience_Campaign_Ad_Preview_Default_ShortHeadline'),
      longHeadline: i18n.getString('Audience_Campaign_Ad_Preview_Default_LongHeadline'),
      description: i18n.getString('Audience_Campaign_Ad_Preview_Default_Description'),
      businessName: i18n.getString('Audience_Campaign_Ad_Preview_Default_BusinessName'),
      url: 'https://www.bing.com',
      callToAction: i18n.getString('Audience_Campaign_Ad_Preview_Default_CallToAction'),
    };

    this.state = {
      isImageSelectorDisplayed: false,
      isImageEditDisplayed: false,
      isBackgroundSceneDialogOpen: false,
      editingImage: null,
      isMMAEnabled: isMultimediaAdinMSANEnabled || isMultimediaAd,
      defaultAddImageIndex: 0,
      imageOption: C.defaultImageOption,
      aspectRatioOption: C.defaultAspectRatioOption,
      addMediaPanelView: VIEWS.LandingPage,
      addImageErrorMessage: null,
      isBackgroundChangeEnabled: false,
      isImageUpscaleEnabled: false,
    };

    this.imageRepository = new ImageRepository(cid, aid, oDataConfig, {
      isAudienceAd,
      isMultimediaAd,
      instrumentation,
      mediaRepository,
      isMultimediaAdinMSANEnabled,
      isImageAdExtensionMode,
      isCombinedMsanAndMmaAspectRatios,
      isMMAV2Enabled,
      adCreatorScenario,
      isMSANBoostEnabled,
      isBoost200x100ImageEnabled,
      isBoostNewAspectRatioSupportEnabled,
      isAudienceAdSmartCropEnabled: permissions.dynamic.AudienceAdSmartCrop,
      isPmaxSmartCropEnabled: permissions.dynamic.PMaxSmartCrop,
      isStoreAppProng,
      isStoreAppSpotLight,
      isStoreAppTransparentLogo,
      isUnifiedAppCampaignWindowsStore,
      isLinkedInAd,
      isImageFittingEnabled,
      isPMaxAspectRatiosV2Enabled,
    });

    if (isAdCreatorEnabled) {
      if (isMultimediaAd) {
        setClarityTag({
          IsAdCreatorV1Enabled: true,
        });
      }
      if (isMultimediaAdinMSANEnabled || isMultimediaAd) {
        setClarityTag({
          IsAdCreatorV2Enabled: true,
        });
      }
    }

    if (_.isFunction(setAddNewImageCallback)) {
      setAddNewImageCallback((imageUrl, imageOption, suggestionId) => this.addImageByUrl(imageUrl, C.imageSources.WebsiteCarousel, imageOption, suggestionId));
    }

    if (_.isFunction(setAddNewImagesCallback)) {
      setAddNewImagesCallback((images, imageOption, replaceExistingAssets) => {
        if (replaceExistingAssets && this.props.imageList.length > 0) {
          this.onRemoveAllImages(imageOption, false /* shouldDirty */);
        }

        return this.addImagesByUrl(images, C.imageSources.WebsiteCarousel, imageOption);
      });
    }

    if (_.isFunction(setOpenAddImageCallback)) {
      setOpenAddImageCallback(() => {
        this.onAddImage(null, 2);
      });
    }

    this.validateImageSizeAsync = validateImageSizeAsync;
    this.validateImageWithDimension = validateImageWithDimension;
    this.getAdCreatorInstrumentation = getAdCreatorInstrumentation;

    this.stockImageDataService = new StockImageDataService({
      cid,
      aid,
      oDataConfig,
      controller: 'StockImages',
    });

    this.investImageDataService = new InvestImageDataService({ cid, aid });

    this.ChangeImageBackgroundDialogLazy = React.lazy(getComponent(getChangeImageBackgroundDialogAsync));

    const socialMediaRepository = new SocialMediaRepository({ cid, aid, oDataConfig });

    if (isMSANBoostEnabled && permissions.IsBoostImageAltTextEnabled) {
      this.folderODataService = getFolderODataService({
        cid,
        aid,
        oDataConfig: {
          ...oDataConfig,
          oDataPath: oDataConfig.odataPath,
          oDataToken: oDataConfig.odataToken,
        },
        odata,
        legacyOdata,
        i18n,
        dateFormat: props.dateFormat,
      });
    }

    this.socialMediaDataSource = new SocialMediaDataSource({ repository: socialMediaRepository });

    this.assetLibraryConfig = {
      aid,
      cid,
      oDataConfig,
      dateFormat: props.dateFormat,
      timeZone: props.timeZone,
      preferencesService: props.preferencesService,
      activityLogger: props.activityLogger,
      // eslint-disable-next-line arrow-body-style
      onImageSelected: (image, imageSource = C.imageSources.Existing) => {
        // clearn up the error message
        this.setState({ addImageErrorMessage: null });
        return this.imageRepository.getImageByIdAndProcessAsync({
          image,
          imageType: this.getCurrentImageType(),
          imageSource,
          onError: (e) => {
            this.setState({
              addImageErrorMessage: getImageValidationErrorMessage(e, i18n),
            });
          },
          imageOption: this.state.imageOption,
          aspectRatioOption: this.state.aspectRatioOption,
          onSelectedImagesSet: img => this.onSelectedImagesSet(img),
          isImageFittingEnabled: this.props.isImageFittingEnabled,
          adType: this.props.isImageAdExtensionMode ? 'ImageAdExtension' : undefined,
        });
      },
      // eslint-disable-next-line arrow-body-style
      onImagesSelected: (imageIds, imageSource = C.imageSources.Existing) => {
        // clearn up the error message
        this.setState({ addImageErrorMessage: null });
        return this.imageRepository.getImagesByIdsAndProcessAsync({
          imageIds,
          imageType: this.getCurrentImageType(),
          imageSource,
          onError: (e) => {
            this.setState({
              addImageErrorMessage: getImageValidationErrorMessage(e, i18n),
            });
          },
          imageOption: this.state.imageOption,
          aspectRatioOption: this.state.aspectRatioOption,
          onSelectedImagesSet: img => this.onSelectedImagesSet(img),
          isImageFittingEnabled: this.props.isImageFittingEnabled,
          adType: this.props.isImageAdExtensionMode ? 'ImageAdExtension' : undefined,
        });
      },
      onImageAddError: (e) => {
        const errorMessage = getImageValidationErrorMessage(e, i18n);
        const genericError = (
          <span>
            {this.props.i18n.getString(_TL_('One or more assets failed to upload. Please try again'))}
            <LearnMoreLink linkText={i18n.getString(_TL_('Learn more about asset guidelines and accepted file types.'))} topic="app54823" />
          </span>
        );
        this.setState({
          addImageErrorMessage: errorMessage || genericError,
        });

        return {
          errorMessage,
        };
      },
      enableImageDeleting,
      enableStockMultipleSelection,
      enableInvestMultipleSelection,
      enableWebsiteMultipleSelection,
    };

    // default open stock image tab if edit from recommendation page
    this.defaultAddImageIndex = _.isEmpty(this.props.recommendation) ? 0 : 1;

    this.maxImageCount = customMaxImageCount || C.DEFAULT_MAX_IMAGE;
  }

  static getDerivedStateFromProps(nextProps, state) {
    if ((state.isMMAEnabled || nextProps.isImageExtensionsEnhancementsEnabled)
      && nextProps.cropImage) {
      const editingImage = nextProps.cropImage.clone();
      const imageOption = _.result(editingImage, 'isLogo') ?
        C.imageOptionKeys.Logo : C.imageOptionKeys.Image;
      return {
        isImageEditDisplayed: true,
        editingImage,
        imageOption,
      };
    } else if ((nextProps.isImageExtensionsEnhancementsEnabled && nextProps.replaceImage)) {
      return {
        isImageSelectorDisplayed: true,
        editingImage: nextProps.replaceImage.clone(),
      };
    }

    return null;
  }

  componentDidMount() {
    const isAllUploadFlowImageUpscaleEnabled = _.get(this.props.permissions, ['dynamic', 'AllUploadFlowImageUpscale']);
    if (this.props.showCopilotOptionForImage || this.props.supportImageUpscale || isAllUploadFlowImageUpscaleEnabled) {
      isCopilotRegionRestricted({
        feature: 'ImageEnhancements',
        permissions: this.props.permissions,
        serverContext: this.props.serverContext,
      })
        .then((isRestricted) => {
          if (!isRestricted) {
            this.setState({
              isBackgroundChangeEnabled: this.props.showCopilotOptionForImage,
              isImageUpscaleEnabled: this.props.supportImageUpscale || isAllUploadFlowImageUpscaleEnabled,
            });
          }
        });
    }
    this.logToSubstrate('image experience mount');
  }

  // eslint-disable-next-line react/sort-comp
  findImageListIndexWithImageId = (imageId, isLogo = false) => (
    _.findIndex(
      this.props.imageList,
      placements => _.any(placements, (placement) => {
        if (this.props.isAdCreatorEnabled && !this.state.editingImage.type) {
          return placement.BaseId === this.state.editingImage.Id && placement.isLogo === isLogo;
        }

        return placement.Id === imageId && placement.isLogo === isLogo;
      })
    )
  );

  findImageIndexWithType = (imageList, imageType) =>
    _.findIndex(imageList, item => item.Type === imageType);


  onAddImage = (event, defaultIndex = 0, imageOption = C.defaultImageOption) => {
    const WEBSITE_IMAGE_INDEX = 2;

    this.logToSubstrate(`open add image panel, imageOption: ${imageOption}`);
    setClarityTag({ OpenAddImagePanel: imageOption });

    if (imageOption === C.imageOptionKeys.Logo) {
      const { addLogoActivity } =
        this.getAdCreatorInstrumentation(this.props.instrumentation, this.props.adCreatorScenario);
      addLogoActivity.trace('UserAddedLogo');
    }

    if (!this.state.isMMAEnabled) {
      this.props.onImageListUpdated([]);
    }

    let addMediaPanelView;

    switch (defaultIndex) {
      case WEBSITE_IMAGE_INDEX:
        // support for image suggestion carousel opening the website images source
        addMediaPanelView = VIEWS.WebsiteImage;
        break;
      default:
        addMediaPanelView = VIEWS.LandingPage;
        break;
    }

    this.setState({
      isImageSelectorDisplayed: true,
      defaultAddImageIndex: defaultIndex,
      addMediaPanelView,
      imageOption,
    });
  };

  onSelectedImagesSet = (images) => {
    if (this.state.isMMAEnabled) {
      const { imageList } = this.props;
      if (this.state.editingImage) {
        // not support to replace aspect ratio
      } else {
        let newImageList = _.clone(imageList);
        let currentSelectedImageId;
        const imagesToAdd = _.isArray(images[0]) ? images : [images];

        // Determine if image/logo/portrait is being added by first placement of first image
        // Assuming all placements in the same image set have the same type
        const firstPlacement = _.chain(imagesToAdd).first().first().value();
        const { isLogo, isPortrait } = firstPlacement;

        _.each(imagesToAdd, (selectedImagePlacements) => {
          const { index: indexToReplace, targetId } = this.findImageSetIndexById({
            imageList: newImageList,
            targetImageSet: selectedImagePlacements,
            isLogo,
            isPortrait,
          });
          currentSelectedImageId = targetId;
          // add associationType to the different ratio logo's id. It use to distinguish the already added logo.
          if (isLogo) {
            const existedLogoIndex = _.findIndex(newImageList, item => (item[0].IdString === selectedImagePlacements[0].IdString && item[0].associationType === selectedImagePlacements[0].associationType && item[0].isIncluded === selectedImagePlacements[0].isIncluded && item[1].isIncluded === selectedImagePlacements[1].isIncluded));
            const currentSelectedRatio = _.find(selectedImagePlacements, logo => logo.isIncluded);
            const currentAssociationType = currentSelectedRatio.associationType;
            const selectedLogoList = newImageList.concat([selectedImagePlacements]);
            const logoList = this.getImageCountInLogoIds(selectedLogoList);
            if (logoList.length > C.DEFAULT_MAX_LOGO) return;
            selectedImagePlacements.forEach((logo) => {
              // eslint-disable-next-line no-param-reassign
              logo.id = `${logo.id}-${currentAssociationType}`;
              currentSelectedImageId = logo.isIncluded ? logo.id : currentSelectedImageId;
            });
            if (existedLogoIndex === -1) {
              newImageList = newImageList.concat([selectedImagePlacements]);
            } else {
              newImageList[existedLogoIndex] = selectedImagePlacements;
            }
          } else if (indexToReplace !== -1) {
            newImageList[indexToReplace] = selectedImagePlacements;
          } else {
            newImageList = newImageList.concat([selectedImagePlacements]);
          }
        });

        this.props.onImageListUpdated(newImageList, currentSelectedImageId, isLogo);
      }
    } else if (this.state.editingImage) {
      this.logToSubstrate('edited or replaced an image');
      const imageToReplace = _.find(images, image => image.Type === this.state.editingImage.Type);

      this.replaceImage(imageToReplace);
    } else {
      this.props.onImageListUpdated(images);
    }
    this.logToSubstrate('selected an image');
    this.setState({
      isImageSelectorDisplayed: false,
      editingImage: null,
      addImageErrorMessage: null,
    });
  };

  onImageSelectionCanceled = () => {
    this.props.onImageReplaceCancelled();

    this.setState({
      isImageSelectorDisplayed: false,
      editingImage: null,
      addImageErrorMessage: null,
    });
  };

  onReplaceImage = (imageType) => {
    this.logToSubstrate('replace an image');
    const imageToEdit = _.find(this.props.imageList, image => image.Type === imageType);

    this.setState({
      isImageSelectorDisplayed: true,
      editingImage:
        imageToEdit ?
          imageToEdit.clone() :
          new ImageExperienceImage({ type: imageType }),
    });
  };

  onReCropImage = (imageType) => {
    this.logToSubstrate('recrop image');
    const imageToEdit = _.find(this.props.imageList, image => image.Type === imageType);
    const editingImage = imageToEdit.clone();
    const imageOption = this.getImageOptionOfImage(editingImage);

    this.setState({
      isImageEditDisplayed: true,
      editingImage,
      imageOption,
    });
  };

  onSelectImage = (imageId, isLogo = false) => {
    this.logToSubstrate('select one image set');
    this.setState({
      editingImage: null,
      imageOption: isLogo ? C.imageOptionKeys.Logo : C.imageOptionKeys.Image,
    });

    this.props.onImageSelected(imageId, isLogo);
  };

  onRemoveImage = (imageId, isLogo = false, isPortrait = false) => {
    let imageOptionUsedForLog = C.imageOptionKeys.Image;
    if (isLogo) {
      imageOptionUsedForLog = C.imageOptionKeys.Logo;
    } else if (isPortrait) {
      imageOptionUsedForLog = C.imageOptionKeys.Portrait;
    }

    this.logToSubstrate(`remove one image set, imageOption: ${imageOptionUsedForLog}`);
    this.setState({
      editingImage: null,
    });

    const { imageList } = this.props;
    const imageToRemove = this.findImageSetById(imageId, isLogo, isPortrait);
    const newImageList = _.without(imageList, imageToRemove);

    this.props.onImageRemoved(imageId, newImageList, isLogo);
    this.props.onImageListUpdated(newImageList, undefined, isLogo);
  };

  onRemoveAllImages = (imageOption = C.defaultImageOption, shouldDirty = true) => {
    this.logToSubstrate('remove all images');
    this.setState({
      editingImage: null,
      imageOption,
    });

    if (this.props.isMultimediaAd || this.props.isLogoEnabled) {
      const { imageList } = this.props;
      const imageListToRemove = _.map(this.getImageList(imageOption), imageArr =>
        this.findImageSetById(this.getFirstElement(imageArr).id, imageOption === C.imageOptionKeys.Logo, imageOption === C.imageOptionKeys.Portrait));
      const newImageList = _.without(imageList, ...imageListToRemove);

      this.props.onImageListUpdated(newImageList);
    } else {
      this.props.onImageListUpdated([], undefined /* currentSelectedImageId */, undefined /* isLogo */, shouldDirty);
    }
  };

  onSaveEditImage = (saveImgObj) => {
    if (this.props.customOnSaveEditImage) {
      this.props.customOnSaveEditImage(saveImgObj);
    } else if (this.props.isAdCreatorEnabled) {
      const {
        effectSettings,
        cropSettings,
        isLogo,
        updatedAltText,
      } = saveImgObj;

      const {
        imageList,
        isMultimediaAd,
        isMultimediaAdinMSANEnabled,
        isCombinedMsanAndMmaAspectRatios,
        isMMAV2Enabled,
        isBoost200x100ImageEnabled,
        isBoostNewAspectRatioSupportEnabled,
        isStoreAppProng,
        isStoreAppSpotLight,
        isStoreAppTransparentLogo,
        isUnifiedAppCampaignWindowsStore,
        isLinkedInAd,
        isPMaxAspectRatiosV2Enabled,
      } = this.props;

      const imageIndex = this.findImageListIndexWithImageId(this.state.editingImage.Id, isLogo);
      const newImageList = [...imageList];
      let newImagePlacements = [...newImageList[imageIndex]];

      if (this.state.editingImage.Type) {
        // placement level edit
        const existingPlacementIndex =
          this.findImageIndexWithType(newImagePlacements, this.state.editingImage.Type);

        const newConfig = newImagePlacements[existingPlacementIndex].clone();

        newConfig.setEffectSettings(effectSettings);
        newConfig.setCropSettings(this.props.isImageFittingEnabled
          ? { ...cropSettings, CroppingType: C.CroppingType.ManualCrop }
          : cropSettings);

        newImagePlacements[existingPlacementIndex] = newConfig;

        newImageList[imageIndex] = newImagePlacements;

        this.props.onImageListUpdated(newImageList);
        if (!_.result(this.state.editingImage, 'isLogo')) {
          this.props.onImagePreviewRequested(this.state.editingImage.Type);
        }
      } else {
        // image level edit
        const {
          cropSettings: previousEditingImageCropSettings,
        } = getEditingMediaProps({
          editingImage: this.state.editingImage,
          isMultimediaAd: this.props.isMultimediaAd,
          imageOption: this.state.imageOption,
          isCombinedMsanAndMmaAspectRatios,
          isPMaxAspectRatiosV2Enabled,
        });

        if (_.isEqual(cropSettings, previousEditingImageCropSettings)) {
          newImagePlacements = _.map(newImagePlacements, (placement) => {
            const fieldsToKeep = {
              associationType: placement.associationType,
              isIncluded: placement.IsIncluded,
              type: placement.Type,
              cropSettings: placement.cropSettings,
              source: placement.Source,
              isLogo: placement.isLogo,
            };

            return new ImageExperienceImage(_.defaults(fieldsToKeep, {
              ...placement.BaseImage.getBasicData(),
              isIncluded: placement.IsIncluded,
              type: placement.Type,
              cropSettings: placement.cropSettings,
            }));
          });
        } else {
          newImagePlacements = getAutoCroppedImages({
            originalImageToCrop: this.state.editingImage,
            isMultimediaAd,
            isMultimediaAdinMSANEnabled,
            maxCropBox: cropSettings,
            imageOption: this.state.imageOption,
            isCombinedMsanAndMmaAspectRatios,
            isRecommendedImage: this.state.editingImage.isRecommendedImage,
            isMMAV2Enabled,
            isBoost200x100ImageEnabled,
            isBoostNewAspectRatioSupportEnabled,
            isStoreAppProng,
            isStoreAppSpotLight,
            isStoreAppTransparentLogo,
            isUnifiedAppCampaignWindowsStore,
            isLinkedInAd,
            isPMaxAspectRatiosV2Enabled,
          });
        }

        _.each(newImagePlacements, (model) => {
          // update placement effect to be same as parent
          model.setEffectSettings(effectSettings);

          if (this.props.isImageFittingEnabled) {
            model.setCropSettings({ ...model.cropSettings, CroppingType: C.CroppingType.ManualCrop });
          }

          // update base image info
          model.BaseImage.setEffectSettings(effectSettings);
          model.BaseImage.setCropSettings(this.props.isImageFittingEnabled
            ? { ...cropSettings, CroppingType: C.CroppingType.ManualCrop }
            : cropSettings);

          model.setAltText(updatedAltText);
          model.BaseImage.setAltText(updatedAltText);
        });

        newImageList[imageIndex] = newImagePlacements;

        if (this.props.isMSANBoostEnabled && this.props.permissions.IsBoostImageAltTextEnabled) {
          const asset = transformAltTextRequest({ assetId: this.state.editingImage.Id, folderAssetType: 'Image', text: updatedAltText });
          // TODO handle async
          this.folderODataService.updateAsset(asset);
        }

        this.props.onImageListUpdated(newImageList);
      }
    } else if (this.state.isMMAEnabled) {
      this.replaceMultimediaImage(saveImgObj);
    } else {
      this.logToSubstrate('edited or recropped an image');
      this.replaceImage(saveImgObj);
    }

    this.setState({
      isImageEditDisplayed: false,
      editingImage: null,
    });
  };

  onCancelEditImage = () => {
    this.props.onImageEditCancelled();

    this.props.onImageCropCancelled();

    this.setState({
      isImageEditDisplayed: false,
      editingImage: null,
    });
  };

  onEditImage = (baseImageId, isLogo = false, isPortrait = false) => {
    let imageOptionUsedForLog = C.imageOptionKeys.Image;
    if (isLogo) {
      imageOptionUsedForLog = C.imageOptionKeys.Logo;
    } else if (isPortrait) {
      imageOptionUsedForLog = C.imageOptionKeys.Portrait;
    }

    this.logToSubstrate(`edit image set, imageOption: ${imageOptionUsedForLog}`);
    setClarityTag({ OpenEditImagePanel: imageOptionUsedForLog });

    const { editImageActivity } = this.getAdCreatorInstrumentation(this.props.instrumentation, this.props.adCreatorScenario);
    const imageSetToEdit = this.findImageSetById(baseImageId, isLogo);
    if (imageSetToEdit && imageSetToEdit[0] && imageSetToEdit[0].BaseImage) {
      let editingImage = imageSetToEdit[0].BaseImage.clone();
      const imageOption = this.getImageOptionOfImage(editingImage);
      if (imageOption === C.imageOptionKeys.Logo) {
        editingImage = _.filter(imageSetToEdit, image => image.isIncluded)[0].clone();
      }

      this.setState({
        isImageEditDisplayed: true,
        editingImage,
        imageOption,
      });
      editImageActivity.trace('User opened edit image panel');
    } else {
      editImageActivity.trace(`unexpected error when opening edit image panel, baseImageId:${baseImageId}, isLogo:${isLogo}, isPortrait:${isPortrait}`);
    }
  };

  isImageValidForChangeBackground = (editingImage) => {
    const { originalSourceType, source } = editingImage || {};
    if (originalSourceType === 'ShutterStock' || source === 'Stock') {
      return false;
    }

    return true;
  };

  onChangeBackgroundScene = (baseImageId) => {
    this.logToSubstrate('change image background scene');
    const imageSetToEdit = this.findImageSetById(baseImageId, false);
    const editingImage = imageSetToEdit[0].BaseImage.clone();
    const imageOption = this.getImageOptionOfImage(editingImage);

    if (this.isImageValidForChangeBackground(editingImage)) {
      this.setState({
        isBackgroundSceneDialogOpen: true,
        copilotEditingImage: editingImage,
        imageOption,
      });
    } else {
      notificationApi.showWarning(this.props.i18n.getString(_TL_('We’re unable to modify the background for stock images from Shutterstock.')));
    }
  };

  onApplyBackgroundScene = (newImageUrl, { shouldUpload = false } = {}) => {
    if (!newImageUrl) {
      return Promise.reject();
    }

    this.setState({
      copilotApplying: true,
    });

    const preHandler = shouldUpload ? this.imageRepository.uploadImageAsync(newImageUrl).then(({
      imageId,
    }) => this.imageRepository.getImageByIdAsync(imageId, false)).then((data) => {
      const { Url } = data;
      return Url;
    }).catch((error) => {
      const errorMessage = getImageValidationErrorMessage(error, this.props.i18n);

      this.setState({
        addImageErrorMessage: errorMessage,
      });

      return {
        errorMessage,
      };
    }) : Promise.resolve(newImageUrl);

    return preHandler.then((preResult) => {
      const nextHandler = preResult && preResult.errorMessage ? Promise.resolve(preResult) : this.addImageByUrl(preResult, C.imageSources.BackgroundChangeGenerated);
      return nextHandler.then((data) => {
        const { errorMessage } = data;
        if (errorMessage) {
          this.setState({
            copilotApplying: false,
            copilotEditingImage: null,
            isBackgroundSceneDialogOpen: false,
          });
          return Promise.reject(errorMessage);
        }

        // replace the old one
        this.onRemoveImage(this.state.copilotEditingImage.Id);

        // close the dialog and reset the state
        this.setState({
          copilotApplying: false,
          copilotEditingImage: null,
          isBackgroundSceneDialogOpen: false,
        });
        return data;
      });
    });
  };

  onCancelApplyBackgroundScene = () => {
    this.setState({
      isBackgroundSceneDialogOpen: false,
      copilotEditingImage: null,
    });
  };

  getImagePathFromGenericImagePath =
    (genericImagePath) => {
      if (_.isString(genericImagePath)) {
        return genericImagePath.substr(0, genericImagePath.indexOf('1.91to1.jpg'));
      }

      return '';
    };

  getFirstElement(item) {
    if (_.isArray(item)) {
      return _.first(item);
    }
    return item;
  }

  getImageList(imageOption = C.defaultImageOption) {
    if (imageOption === C.imageOptionKeys.Logo) {
      return _.filter(this.props.imageList, imageArr =>
        this.getFirstElement(imageArr).isLogo === true);
    } else if (imageOption === C.imageOptionKeys.Portrait) {
      return _.filter(this.props.imageList, imageArr =>
        this.getFirstElement(imageArr).isPortrait === true || this.getFirstElement(imageArr).associationType === 'ImageMedia1x2');
    }
    return _.filter(this.props.imageList, imageArr =>
      this.getFirstElement(imageArr).isLogo !== true && this.getFirstElement(imageArr).isPortrait !== true);
  }

  getImageOptionOfImage(image) {
    if (_.result(image, 'isLogo')) {
      return C.imageOptionKeys.Logo;
    } else if (_.result(image, 'isPortrait')) {
      return C.imageOptionKeys.Portrait;
    }
    return C.imageOptionKeys.Image;
  }

  addImageByUrl = (imageUrl, imageSource = C.imageSources.WebsiteCarousel, imageOption, suggestionId) => {
    const {
      i18n,
    } = this.props;

    this.setState({
      imageOption: imageOption || this.state.imageOption,
    });

    return this.imageRepository.addImageByUrlThenProcessAsync({
      imageUrl,
      suggestionId,
      imageType: this.getCurrentImageType(),
      imageSource,
      onError: (e) => {
        const errorMessage = getImageValidationErrorMessage(e, i18n);

        this.setState({
          addImageErrorMessage: errorMessage,
        });

        return {
          errorMessage,
        };
      },
      imageOption: imageOption || this.state.imageOption,
      aspectRatioOption: this.state.aspectRatioOption,
      onSelectedImagesSet: img => this.onSelectedImagesSet(img),
      isImageFittingEnabled: this.props.isImageFittingEnabled,
      adType: this.props.isImageAdExtensionMode ? 'ImageAdExtension' : undefined,
    });
  };

  addImagesByUrl = (images, imageSource = C.imageSources.WebsiteCarousel, imageOption) => {
    const {
      i18n,
    } = this.props;

    if (images.length === 1) {
      const { suggestionId } = images[0];
      return this.addImageByUrl(images[0].fullUrl, imageSource, imageOption, suggestionId);
    }

    this.setState({
      imageOption: imageOption || this.state.imageOption,
    });

    return this.imageRepository.addImagesByUrlThenProcessAsync({
      images,
      imageType: this.getCurrentImageType(),
      imageSource,
      onError: (errors) => {
        const errorMessage = getMultiAddedImageErrorMessage(errors, i18n);

        this.setState({
          addImageErrorMessage: errorMessage,
        });

        return {
          errorMessage,
        };
      },
      imageOption: imageOption || this.state.imageOption,
      aspectRatioOption: this.state.aspectRatioOption,
      onSelectedImagesSet: img => this.onSelectedImagesSet(img),
      isImageFittingEnabled: this.props.isImageFittingEnabled,
    });
  };

  findImageSetIndexById = ({
    imageList,
    baseImageId,
    targetImageSet,
    isLogo = false,
    isPortrait = false,
  }) => {
    let index;
    let targetId;
    const targetIsLogo = _.result(_.first(targetImageSet), 'isLogo') || isLogo;

    const targetIsPortrait = _.result(_.first(targetImageSet), 'isPortrait') || isPortrait;

    if (this.props.isAdCreatorEnabled) {
      targetId = baseImageId || targetImageSet[0].BaseId;

      index = _.findIndex(imageList, (placements) => {
        const firstPlacement = _.first(placements);

        // find id of logo/portrait only when the target is a logo or portrait, else just return the first index of the placements
        if (targetIsLogo) {
          return firstPlacement.BaseId.toString() === targetId.toString() &&
            firstPlacement.isLogo === targetIsLogo;
        }

        if (targetIsPortrait) {
          return firstPlacement.BaseId.toString() === targetId.toString() &&
            firstPlacement.isPortrait === targetIsPortrait;
        }

        // return image that is not portrait or logo
        return firstPlacement.BaseId.toString() === targetId.toString() && !firstPlacement.isLogo && !firstPlacement.isPortrait;
      });
    } else {
      targetId = baseImageId || targetImageSet[0].Id;

      index = _.findIndex(imageList, placements =>
        _.any(placements, placement => placement.Id.toString() === targetId.toString()));
    }

    return { index, targetId };
  };

  findImageSetById = (baseImageId, isLogo = false, isPortrait = false) => {
    const { imageList } = this.props;
    const { index } = this.findImageSetIndexById({
      imageList, baseImageId, isLogo, isPortrait,
    });

    return imageList[index];
  };

  logToSubstrate(mes) {
    if (this.props.activityLogger) {
      this.props.activityLogger.signal(mes, 'image experience');
    }
  }

  replaceImage(imageObj) {
    const { imageList } = this.props;

    const existingIndex =
      _.findIndex(imageList, imageInList => imageInList.Type === imageObj.Type);

    const newImageList = [...imageList];

    // replacing already set image
    if (existingIndex >= 0) {
      newImageList[existingIndex] = imageObj;
    } else {
      // insert and sort
      newImageList.push(imageObj);
    }

    this.props.onImageListUpdated(newImageList);

    this.props.onImagePreviewRequested(imageObj.Type);
  }

  replaceMultimediaImage(imageObj) {
    const { imageList } = this.props;

    const imageIndex =
      _.findIndex(imageList, images => _.any(images, image => image.Id === imageObj.Id));

    const newImageList = [...imageList];

    if (imageIndex >= 0) {
      const newImage = newImageList[imageIndex];
      const typeIndex = _.findIndex(newImage, image => image.Type === imageObj.Type);

      if (typeIndex >= 0) {
        newImage[typeIndex] = imageObj;
      }
    } else {
      // not support to add new aspect ratio
    }

    this.props.onImageListUpdated(newImageList);
    this.props.onImagePreviewRequested(imageObj.Type);
  }

  imageListComponent(imageOption = C.defaultImageOption, portalContainer) {
    const { isRenderAddInImageList, customOnEditImage } = this.props;

    const imageList = this.getImageList(imageOption);
    const isEmpty = imageList.length === 0 ||
      (!this.state.isMMAEnabled && !_.some(imageList, image => image.Src));
    const imageListClassName = `${imageOption.toLowerCase()}-list-component`;
    const isLogo = imageOption === C.imageOptionKeys.Logo;
    const isPortrait = imageOption === C.imageOptionKeys.Portrait;

    if (this.state.isMMAEnabled) {
      return (
        <div className={imageListClassName}>
          {!isRenderAddInImageList && this.renderAddImage(imageOption)}
          {(!isEmpty || isRenderAddInImageList) && <ResponsiveImageListView
            imageList={imageList}
            isLogo={isLogo}
            isPortrait={isPortrait}
            i18n={this.props.i18n}
            onEditImage={customOnEditImage || this.onEditImage}
            onChangeBackgroundScene={this.onChangeBackgroundScene}
            onRemoveImage={this.onRemoveImage}
            onRemoveAllImages={() => this.onRemoveAllImages(imageOption)}
            onSelectImage={this.onSelectImage}
            isRenderAddInImageList={isRenderAddInImageList && !this.shouldDisableAddButton(imageOption)}
            onAddImage={() => this.onAddImage(null, this.defaultAddImageIndex, imageOption)}
            currentSelectedImageId={isLogo ?
              this.props.currentSelectedLogoId : this.props.currentSelectedImageId}
            adCreatorScenario={this.props.adCreatorScenario}
            portalContainer={portalContainer}
            blocking={!isLogo && this.state.copilotApplying}
            showCopilotOption={!isLogo && this.state.isBackgroundChangeEnabled}
            enableEdit={this.props.enableImageListEdit}
            logger={this.props.activityLogger}
            error={!isLogo && this.state.isBackgroundChangeEnabled && !this.state.isImageSelectorDisplayed ? this.state.addImageErrorMessage : null}
          />}
        </div>
      );
    }

    return (
      <React.Fragment>
        {isEmpty && this.renderAddImage(imageOption)}
        {!isEmpty && <ImageListView
          isAdPreviewRefreshEnabled={this.props.isAdPreviewRefreshEnabled}
          imageList={imageList}
          i18n={this.props.i18n}
          onReplaceImage={this.onReplaceImage}
          onReCropImage={this.onReCropImage}
          onRemoveAllImages={() => this.onRemoveAllImages(imageOption)}
          isForVNext={this.props.isForVNext}
          isMultimediaAd={this.props.isMultimediaAd}
          permissions={this.props.permissions}
          readOnly={this.props.readOnly}
        />}
      </React.Fragment>
    );
  }

  shouldDisableAddButton(imageOption = C.defaultImageOption) {
    let disable = this.props.readOnly;

    const imageList = this.getImageList(imageOption);

    if (imageList.length >= (imageOption === C.defaultImageOption ? this.maxImageCount : C.DEFAULT_MAX_LOGO)) {
      disable = true;
    }

    if (imageOption === 'Logo' && this.props.isMultimediaAd
      && this.getImageCountInLogoIds(this.props.imageList).length <= C.DEFAULT_MAX_LOGO) {
      const logoList = this.getImageCountInLogoIds(this.props.imageList);
      const logoRatiosCount = Object.keys(C.getMmaAspectRatios('Logo')).length;
      disable = imageList.length === C.DEFAULT_MAX_LOGO * logoRatiosCount && logoList.length === C.DEFAULT_MAX_LOGO;
    }

    if (imageOption === C.imageOptionKeys.Portrait) {
      disable = imageList.length >= C.DEFAULT_MAX_PORTRAIT;
    }

    return disable;
  }

  renderAddImage(imageOption = C.defaultImageOption) {
    let tooltipId = 'pop_BA_CampaignCreate_MMA_Logos'; // add logo
    if (imageOption === C.defaultImageOption) { // add image
      if (this.props.isMultimediaAd) {
        tooltipId = 'pop_BA_Assets_Images';
      } else if (this.props.isImageAdExtensionMode) {
        tooltipId = 'pop_BA_ImageAssets_ImageExt';
      } else {
        tooltipId = 'pop_BA_ImageAssets_Audience';
      }
    }

    // apply only for Audience MMA
    if (this.props.isAudienceMMAWithLogoAndCTA &&
      imageOption === C.imageOptionKeys.Logo) {
      tooltipId = 'pop_BA_AudAds_Logos';
    }

    return (
      <div>
        <div className="image-section-label">
          <label>
            {imageOption === C.defaultImageOption ?
              this.props.i18n.getString('Import_ImportSummary_Images') :
              this.props.i18n.getString('Import_ImportSummary_Logos')}
          </label>
          {imageOption === C.defaultImageOption ?
            <HelpPopup
              topic={tooltipId}
              aria-label={this.props.i18n.getString('Click to learn more about {{item}}', { item: this.props.i18n.getString('ImageExperience_Image_List_Label') })}
            /> :
            <HelpPopup
              topic={tooltipId}
              aria-label={this.props.i18n.getString('Click to learn more about {{item}}', { item: this.props.i18n.getString('ImageExperience_Logo_List_Label') })}
            />
          }
        </div>
        {this.props.isLogoEnabled &&
          <div className="image-section-description">
            {imageOption === C.defaultImageOption ?
              this.props.i18n.getString(`ImageExperience_LoadImage_Description${this.props.isMultimediaAd ? '_MMA' : ''}`) :
              this.props.i18n.getString('ImageExperience_LoadLogo_Description')}
          </div>
        }
        <button
          className="add-image-button btn btn-hero"
          disabled={this.shouldDisableAddButton(imageOption)}
          onClick={event => this.onAddImage(event, this.defaultAddImageIndex, imageOption)}
        >
          {imageOption === C.defaultImageOption ?
            this.props.i18n.getString('ImageExperience_LoadImage') :
            this.props.i18n.getString('ImageExperience_LoadLogo')}
        </button>
      </div>
    );
  }

  renderPreviewCarouselForRightRail({
    previewImage,
    cropSettings,
    previewTexts,
    hasCropSettingsChanged,
    hasEffectSettingsChanged,
    selectedImageType,
  }) {
    const {
      i18n, isMultimediaAd,
      isMultimediaAdinMSANEnabled,
      isCombinedMsanAndMmaAspectRatios,
      isMMAV2Enabled,
      isBoost200x100ImageEnabled,
      isBoostNewAspectRatioSupportEnabled,
      isMSANBoostEnabled,
      currentSelectedLogoId,
      isStoreAppProng,
      isStoreAppSpotLight,
      isStoreAppTransparentLogo,
      isUnifiedAppCampaignWindowsStore,
      isLinkedInAd,
      isPMaxAspectRatiosV2Enabled,
    } = this.props;

    // get current preview logo, to render it on placement components
    const allLogos = this.getImageList(C.imageOptionKeys.Logo);
    const curLogos = currentSelectedLogoId
      ? _.find(allLogos, logo => this.getFirstElement(logo).Id === currentSelectedLogoId)
      : _.first(allLogos);
    const curPreviewLogo = _.find(curLogos, placement => placement.isIncluded === true) || _.first(curLogos);

    if (previewImage.Type && !isLinkedInAd) {
      previewImage.setCropSettings(cropSettings);
      previewImage.setIncluded(true);

      const aspectRatios = C.getAspectRatios({
        isMultimediaAd,
        isMultimediaAdinMSANEnabled,
        isCombinedMsanAndMmaAspectRatios,
        isMMAV2Enabled,
        isBoost200x100ImageEnabled,
        isBoostNewAspectRatioSupportEnabled,
        isStoreAppProng,
        isStoreAppSpotLight,
        isStoreAppTransparentLogo,
        isUnifiedAppCampaignWindowsStore,
        isPMaxAspectRatiosV2Enabled,
      });
      const aspectRatioConfig = _.get(aspectRatios, previewImage.type, {});

      return (
        <PreviewSingleRightRailWrapper
          imageObj={previewImage}
          previewTexts={previewTexts}
          previewLogo={curPreviewLogo}
          i18n={i18n}
          previewTitle={i18n.getString('ImageExperience_Ad_Preview_Title')}
          previewSettings={aspectRatioConfig.previewSettings}
          Preview={this.props.enableUnifiedAdPreview ? UnifiedAdPreviewSinglePlacementWrapper : PreviewGeneral}
          adCreatorScenario={this.props.adCreatorScenario}
          isMultimediaAd={isMultimediaAd}
          isCombinedMsanAndMmaAspectRatios={isCombinedMsanAndMmaAspectRatios}
          isMMAV2Enabled={isMMAV2Enabled}
          isBoost200x100ImageEnabled={isBoost200x100ImageEnabled}
          adPreviewConfig={this.props.adPreviewConfig}
          isBoostNewAspectRatioSupportEnabled={isBoostNewAspectRatioSupportEnabled}
          enableUnifiedAdPreview={this.props.enableUnifiedAdPreview}
          placementType={this.props.editingPlacementType}
        />
      );
    }

    const imageListForPreview = getAutoCroppedImages({
      originalImageToCrop: previewImage,
      isMultimediaAd: this.props.isMultimediaAd,
      isMultimediaAdinMSANEnabled: this.props.isMultimediaAdinMSANEnabled,
      maxCropBox: cropSettings,
      isMMAV2Enabled,
      isBoost200x100ImageEnabled,
      isBoostNewAspectRatioSupportEnabled,
      isLinkedInAd,
    });

    const currentImageList =
      this.props.imageList[this.findImageListIndexWithImageId(
        this.state.editingImage.Id,
        this.state.imageOption === C.imageOptionKeys.Logo
      )];

    imageListForPreview.forEach((image) => {
      image.setIncluded(true);

      if (!hasCropSettingsChanged || !hasEffectSettingsChanged) {
        const currentImage =
          currentImageList[this.findImageIndexWithType(currentImageList, image.Type)]; // eslint-disable-line max-len

        if (currentImage) {
          if (!hasCropSettingsChanged && currentImage.cropSettings) {
            image.setCropSettings(currentImage.cropSettings);
          }

          if (!hasEffectSettingsChanged && currentImage.effectSettings) {
            image.setEffectSettings(currentImage.effectSettings);
          }
        }
      }
    });

    const adPreviewConfig = isLinkedInAd
      ? ({
        ...this.props.adPreviewConfig,
        placementConfig: _.where(this.props.adPreviewConfig.placementConfig, { placementSetName: selectedImageType }),
      }) : this.props.adPreviewConfig;

    return (
      <PreviewCarouselRightRailWrapper
        key={selectedImageType}
        imageListForPreview={imageListForPreview}
        previewTexts={previewTexts}
        previewLogo={curPreviewLogo}
        imageListLength={this.props.imageList.length}
        activityLogger={this.props.activityLogger}
        genericImage={this.props.genericImage}
        i18n={i18n}
        // eslint-disable-next-line no-nested-ternary
        PreviewCarousel={this.props.enableUnifiedAdPreview ? UnifiedAdPreviewCarouselLayoutWrapper : (isMultimediaAd ? PreviewCarousel : MMAPreviewCarousel)}
        adCreatorScenario={this.props.adCreatorScenario}
        isMultimediaAd={isMultimediaAd}
        isMMAV2Enabled={isMMAV2Enabled}
        isMSANBoostEnabled={isMSANBoostEnabled}
        isBoost200x100ImageEnabled={isBoost200x100ImageEnabled}
        isBoostNewAspectRatioSupportEnabled={isBoostNewAspectRatioSupportEnabled}
        permissions={this.props.permissions}
        adPreviewConfig={adPreviewConfig}
        enableUnifiedAdPreview={this.props.enableUnifiedAdPreview}
      />
    );
  }

  getCurrentImageType() {
    return this.state.editingImage ? this.state.editingImage.Type : null;
  }

  uploadImage({
    imageData,
    updateCurrentProgress,
    name,
  }) {
    return this.imageRepository.uploadImageWithValidationAsync({
      imageData,
      updateCurrentProgress,
      name,
      imageOption: this.state.imageOption,
      imageType: this.getCurrentImageType(),
    });
  }

  processUploadedImage({
    imageId,
    index,
    updateCurrentProgress,
  }) {
    const callback = () => updateCurrentProgress(index, 1);
    const imageSource = C.imageSources.Upload;

    return this.imageRepository.getImageByIdAndProcessAsync({
      image: { id: imageId },
      imageType: this.getCurrentImageType(),
      imageSource,
      onError: (e) => {
        this.setState({
          addImageErrorMessage: getImageValidationErrorMessage(e, this.props.i18n),
        });
      },
      imageOption: this.state.imageOption,
      aspectRatioOption: this.state.aspectRatioOption,
      onSelectedImagesSet: img => this.onSelectedImagesSet(img),
      optionalCallback: callback,
      isImageFittingEnabled: this.props.isImageFittingEnabled,
      adType: this.props.isImageAdExtensionMode ? 'ImageAdExtension' : undefined,
    });
  }

  onProcessUploadComplete(args = []) {
    const upscaledImages = _.filter(args, item => _.result(item, 'isImageUpscaled') === true);
    const count = upscaledImages.length;

    if (count === 1) {
      const imageName = _.get(upscaledImages, ['0', 'text']);
      if (imageName) {
        notificationApi.showInfo(this.props.i18n.getString(_TL_('We upscaled {{fileName}} to meet the minimum required file size. Please note that you can also upload a higher quality image if needed.'), { fileName: imageName }));
      }
    } else if (count > 1) {
      notificationApi.showInfo(this.props.i18n.getString(_TL_('We upscaled {{fileCount}} images to meet the minimum required file size. Please note that you can also upload higher quality images if needed.'), { fileCount: count }));
    }
  }

  selectStockImage({ selectedImageId }) {
    const imageSource = C.imageSources.Stock;

    return this.stockImageDataService.saveStockImage({
      stockImageId: selectedImageId,
      stockImageVendorId: StockImageVendor.ShutterStock,
    })
      // eslint-disable-next-line arrow-body-style
      .then(({ value }) => {
        return this.imageRepository.getImageByIdAndProcessAsync({
          image: { id: value },
          imageType: this.getCurrentImageType(),
          imageSource,
          onError: (e) => {
            this.setState({
              addImageErrorMessage: getImageValidationErrorMessage(e, this.props.i18n),
            });
          },
          imageOption: this.state.imageOption,
          aspectRatioOption: this.state.aspectRatioOption,
          onSelectedImagesSet: img => this.onSelectedImagesSet(img),
          isImageFittingEnabled: this.props.isImageFittingEnabled,
          adType: this.props.isImageAdExtensionMode ? 'ImageAdExtension' : undefined,
        });
      });
  }

  getCustomConfigForImageSources = ({ addMediaConfig }) => {
    const {
      permissions,
      isImageAdExtensionMode,
      isAdCreatorEnabled,
      isFinalUrlImageRetrievalEnabled,
    } = this.props;
    let availableImageSources = addMediaConfig;

    if (!permissions.IsSocialMediaImagesEnabled || isImageAdExtensionMode) {
      availableImageSources = _.omit(availableImageSources, VIEWS.SocialMedia);
    }

    if (!isAdCreatorEnabled && !isFinalUrlImageRetrievalEnabled) {
      availableImageSources = _.omit(availableImageSources, VIEWS.WebsiteImage);
    }

    return availableImageSources;
  };

  getImageCountInLogoIds(logoImageList) {
    const logoIds = [];
    if (!logoImageList || logoImageList.length === 0) return logoIds;
    _.each(logoImageList, (item) => {
      if (item[0].isLogo) {
        logoIds.push(item[0].IdString);
      }
    });
    return _.uniq(logoIds);
  }

  render() {
    let previewTexts = {
      longHeadline: _.isEmpty(_.get(this.props, 'longHeadline')) ?
        this.defaultPreviewFields.longHeadline : this.props.longHeadline,
      shortHeadline: _.isEmpty(_.get(this.props, 'shortHeadline')) ?
        this.defaultPreviewFields.shortHeadline : this.props.shortHeadline,
      description: _.isEmpty(_.get(this.props, 'description')) ?
        this.defaultPreviewFields.description : this.props.description,
      businessName: _.isEmpty(_.get(this.props, 'businessName')) ?
        this.defaultPreviewFields.businessName : this.props.businessName,
      url: _.isEmpty(_.get(this.props, 'url')) ?
        this.defaultPreviewFields.url : this.props.url,
      extensionHeadline: this.props.extensionHeadline,
      callToAction: this.props.callToAction && this.props.callToAction.length > 0 ?
        this.props.callToAction : this.defaultPreviewFields.callToAction,
    };

    if (this.props.customPreviewTexts) {
      previewTexts = this.props.customPreviewTexts;
    }

    const {
      i18n,
      newI18n,
      cid,
      aid,
      oDataConfig,
      isStockPhotoEnabled,
      isForVNext,
      isLazyLoadEnabled,
      permissions,
      perfMarker,
      isImageAdExtensionMode,
      isMultimediaAdinMSANEnabled,
      isCombinedMsanAndMmaAspectRatios,
      isLogoEnabled,
      isBoostPortraitEnabled,
      isBusinessLogo,
      genericImage,
      placeHolderImages,
      isVideo,
      isMultimediaAd,
      serverContext,
      isAdCreatorEnabled,
      imagesPortalContainer,
      logosPortalContainer,
      portraitPortalContainer,
      isMMAV2Enabled,
      isBulkEditMMA,
      isBoost200x100ImageEnabled,
      isBoostNewAspectRatioSupportEnabled,
      disableThumbsUpDown,
      isStoreAppProng,
      isStoreAppSpotLight,
      isStoreAppTransparentLogo,
      isUnifiedAppCampaignWindowsStore,
      assetLibraryEventHandler,
      isLinkedInAd,
      isPMaxAspectRatiosV2Enabled,
    } = this.props;

    if (isVideo) {
      return <VideoExperience {...{ ...this.props, perfMarker: perfMarker.createChild('videoExperience') }} />;
    }

    const isThumbsUpDownEnabled = isAdCreatorEnabled && !serverContext.IsAADUser && !disableThumbsUpDown;
    const isTestEnv = isTestEnvironment(document.domain);

    const aspectRatioConfig =
      C.getAspectRatios({
        isMultimediaAd,
        isMultimediaAdinMSANEnabled,
        isCombinedMsanAndMmaAspectRatios,
        imageOption: this.state.imageOption,
        isMMAV2Enabled,
        isBoost200x100ImageEnabled,
        isBoostNewAspectRatioSupportEnabled,
        isStoreAppProng,
        isStoreAppSpotLight,
        isStoreAppTransparentLogo,
        isLinkedInAd,
        isUnifiedAppCampaignWindowsStore,
        isPMaxAspectRatiosV2Enabled,
      });

    const imageOptions = C.getImageOptions({
      i18n,
      disableImage: this.getImageList(C.imageOptionKeys.Image).length >= this.maxImageCount,
      disableLogo: this.getImageList(C.imageOptionKeys.Logo).length >= C.DEFAULT_MAX_LOGO,
      disablePortrait: this.getImageList(C.imageOptionKeys.Portrait).length >= 1,
    });

    const imageList = this.getImageList(this.state.imageOption);
    const imageListLength = _.isArray(imageList) ? imageList.length : 0;
    let maxUploadNum = 1;

    const imageType = this.getCurrentImageType();
    const minSize = C.getMinimumSizeForImage(
      imageType,
      isMultimediaAd,
      this.state.imageOption,
      isCombinedMsanAndMmaAspectRatios,
      isMMAV2Enabled,
      undefined, // isDisplayAds
      undefined, // isPortraitImage
      isBoost200x100ImageEnabled,
      isBoostNewAspectRatioSupportEnabled,
      isStoreAppProng,
      isStoreAppSpotLight,
      isStoreAppTransparentLogo,
      isLinkedInAd,
      isUnifiedAppCampaignWindowsStore,
      isPMaxAspectRatiosV2Enabled
    );

    let minUploadSize = [];

    if (this.state.isImageUpscaleEnabled && minSize.length > 1 && this.state.imageOption === C.imageOptionKeys.Image) {
      // Only when supportImageUpscale is true, we need to set minUploadSize
      // Otherwise, minUploadSize should not be defined, as will use minSize directly
      if (minSize[0] >= 352 && minSize[0] <= 703 && minSize[1] >= 184 && minSize[1] <= 368) {
        minUploadSize = [352, 184]; // It's a half of constants.DefaultImageMinimumSize 703x368
      }
    }


    if (isAdCreatorEnabled) {
      if (this.state.imageOption === C.defaultImageOption) {
        maxUploadNum = this.maxImageCount - imageListLength;
      } else if (this.state.imageOption === C.imageOptionKeys.Portrait) {
        maxUploadNum = 1 - imageListLength;
      } else {
        maxUploadNum = C.DEFAULT_MAX_LOGO - imageListLength;
      }
    }

    const imageExperienceElement = (
      <div className="image-experience-content" style={isBulkEditMMA ? { minWidth: 'inherit' } : {}}>
        {
          !isBusinessLogo && this.imageListComponent(C.imageOptionKeys.Image, imagesPortalContainer)
        }
        {isLogoEnabled &&
          <div className="image-required-error error" role="alert">
            {
              this.props.imageErrorMessages.map(errMsg => (
                <div className="error">
                  {errMsg}
                </div>
              ))
            }
          </div>
        }
        {isLogoEnabled &&
          this.imageListComponent(C.imageOptionKeys.Logo, logosPortalContainer)
        }
        {
          isBoostPortraitEnabled &&
          this.imageListComponent(C.imageOptionKeys.Portrait, portraitPortalContainer)
        }
        <AddImageCrudAdComponent
          isAADUser={serverContext && serverContext.IsAADUser}
          isLiteLandingPageEnabled={this.props.isLiteLandingPageEnabled}
          isOpen={this.state.isImageSelectorDisplayed &&
            this.state.imageOption !== C.imageOptionKeys.Logo}
          setIsOpen={(newOpen) => {
            this.setState({
              isImageSelectorDisplayed: newOpen,
              addImageErrorMessage: null,
            });
          }}
          perfMarker={perfMarker.createChild('imagePicker')}
          uploadImage={args => this.uploadImage(args)}
          processUploadedImage={args => this.processUploadedImage(args)}
          onProcessUploadComplete={args => this.onProcessUploadComplete(args)}
          minSize={minSize}
          maxUploadNum={maxUploadNum}
          imageExperienceProps={{
            suggestion: this.props.suggestion,
            finalUrl: this.props.finalUrl,
            onUpdateFinalUrl: this.props.onUpdateFinalUrl,
            finalUrlProtocol: this.props.finalUrlProtocol,
            onUpdateFinalUrlPrefix: this.props.onUpdateFinalUrlPrefix,
            suggestionPoller: this.props.suggestionPoller,
            isMultimediaAd,
            imageOption: this.state.imageOption,
            isMMAV2Enabled,
            isStoreAppProng,
            isStoreAppSpotLight,
            isStoreAppTransparentLogo,
            isUnifiedAppCampaignWindowsStore,
          }}
          addImageByUrl={(url, imageSource) => this.addImageByUrl(url, imageSource)}
          managedViewSelectionState={this.state.addMediaPanelView}
          updateManagedViewSelectionState={newView => this.setState({
            addMediaPanelView: newView,
          })}
          stockImageConfig={{
            getStockImages: args => this.stockImageDataService.getStockImages(args),
            onSelectStockImage: args => this.selectStockImage(args),
          }}
          investImageConfig={{
            getInvestImages: args => this.investImageDataService.getInvestAssets(args),
          }}
          socialImageConfig={{
            socialMediaDataSource: this.socialMediaDataSource,
            activityLogger: this.props.activityLogger,
          }}
          customMediaSourceConfig={this.getCustomConfigForImageSources}
          assetLibraryConfig={this.assetLibraryConfig}
          i18n={(newI18n instanceof I18nModel) ? newI18n : i18n}
          cid={cid}
          aid={aid}
          errorMessage={this.state.addImageErrorMessage}
          imageType={this.state.editingImage ? this.state.editingImage.Type : null}
          activityLogger={this.props.activityLogger}
          isAssetLibraryAIGCMVPEnabled={isStoreAppProng || isStoreAppSpotLight || isStoreAppTransparentLogo || isUnifiedAppCampaignWindowsStore ? false : undefined}
          isStockImageEnabled={this.props.isStockImageEnabled}
          permissions={permissions}
          eventHandler={assetLibraryEventHandler}
          supportImageUpscale={this.state.isImageUpscaleEnabled && !_.isEmpty(minUploadSize)}
          minUploadSize={minUploadSize}
          serverContext={this.props.serverContext}
        />
        <AddImageContainer
          isImageSelectorDisplayed={this.state.isImageSelectorDisplayed &&
            this.state.imageOption === C.imageOptionKeys.Logo}
          onSelectedImagesSet={this.onSelectedImagesSet}
          onImageSelectionCanceled={this.onImageSelectionCanceled}
          imageType={this.state.editingImage ? this.state.editingImage.Type : null}
          i18n={i18n}
          perfMarker={perfMarker.createChild('imagePicker')}
          isForVNext={isForVNext}
          isMultimediaAd={this.props.isMultimediaAd}
          isAudienceAd={this.props.isAudienceAd}
          cid={cid}
          aid={aid}
          isStockPhotoEnabled={isStockPhotoEnabled}
          oDataConfig={oDataConfig}
          isLazyLoadEnabled={isLazyLoadEnabled}
          permissions={permissions}
          isMultimediaAdinMSANEnabled={isMultimediaAdinMSANEnabled}
          staticImagePath={this.getImagePathFromGenericImagePath(this.props.genericImage)}
          imageList={imageList}
          maxUploadNum={this.state.imageOption === C.defaultImageOption ?
            this.maxImageCount : C.DEFAULT_MAX_LOGO}
          imageOption={this.state.imageOption}
          imageOptions={imageOptions}
          setImageOption={option => this.setState({ imageOption: option })}
          aspectRatioOption={this.state.aspectRatioOption}
          setAspectRatioOption={option => this.setState({ aspectRatioOption: option })}
          suggestion={this.props.suggestion}
          suggestionPoller={this.props.suggestionPoller}
          isAdCreatorEnabled={isAdCreatorEnabled}
          isFinalUrlImageRetrievalEnabled={this.props.isFinalUrlImageRetrievalEnabled}
          isLogoEnabled={this.props.isLogoEnabled}
          mediaRepository={this.props.mediaRepository}
          onUpdateFinalUrlPrefix={this.props.onUpdateFinalUrlPrefix}
          onUpdateFinalUrl={this.props.onUpdateFinalUrl}
          finalUrl={this.props.finalUrl}
          finalUrlProtocol={this.props.finalUrlProtocol}
          defaultSelectedPill={this.state.defaultAddImageIndex}
          adCreatorScenario={this.props.adCreatorScenario}
          isThumbsUpDownEnabled={isThumbsUpDownEnabled}
          isTestEnv={isTestEnv}
          activityLogger={this.props.activityLogger}
          recommendationStockImages={!_.isUndefined(this.props.recommendation) &&
            !_.isEmpty(this.props.recommendation.StockImages) ?
            this.props.recommendation.StockImages[0] : {}}
          onUploadStart={this.props.onUploadStart}
          onUploadComplete={this.props.onUploadComplete}
          isImageExtensionsEnhancementsEnabled={this.props.isImageExtensionsEnhancementsEnabled}
          isMMAV2Enabled={this.props.isMMAV2Enabled}
          isStoreAppProng={this.props.isStoreAppProng}
          isStoreAppSpotLight={this.props.isStoreAppSpotLight}
          isStoreAppTransparentLogo={this.props.isStoreAppTransparentLogo}
          isUnifiedAppCampaignWindowsStore={this.props.isUnifiedAppCampaignWindowsStore}
          isLinkedInAd={this.props.isLinkedInAd}
        />
        {
          this.props.isAdCreatorEnabled ?
            <MediaEditorPanel
              isOpen={this.state.isImageEditDisplayed}
              onDismiss={this.onCancelEditImage}
              onApply={this.onSaveEditImage}
              mediaRepository={this.props.mediaRepository}
              isMMAV2Enabled={this.props.isMMAV2Enabled}
              aspectRatioConfig={aspectRatioConfig}
              {...getEditingMediaProps({
                editingImage: this.state.editingImage,
                isMultimediaAd: this.props.isMultimediaAd,
                imageOption: this.state.imageOption,
                isCombinedMsanAndMmaAspectRatios,
                isMMAV2Enabled: this.props.isMMAV2Enabled,
                isStoreAppProng,
                isStoreAppSpotLight,
                isStoreAppTransparentLogo,
                isUnifiedAppCampaignWindowsStore,
                isLinkedInAd: this.props.isLinkedInAd,
                selectedImageType: this.state.selectedImageType,
                isPMaxAspectRatiosV2Enabled: this.props.isPMaxAspectRatiosV2Enabled,
              })}
              isStock={_.get(this.state.editingImage, 'originalSourceType') === 'ShutterStock' || _.get(this.state.editingImage, 'source') === 'Stock'}
              isLogo={this.state.imageOption === C.imageOptionKeys.Logo}
              isPortrait={this.state.imageOption === C.imageOptionKeys.Portrait}
              hasRightRail={this.state.imageOption !== C.imageOptionKeys.Logo}
              permissions={permissions}
              onRenderRightRailContent={({
                previewImageUrl,
                cropSettings,
                hasCropSettingsChanged,
                hasEffectSettingsChanged,
                selectedImageType,
              }) => {
                const baseImage = this.state.editingImage;

                if (selectedImageType !== this.state.selectedImageType) {
                  this.setState({ selectedImageType });
                }

                const previewImage = new ImageExperienceImage({
                  src: previewImageUrl,
                  width: baseImage.Width,
                  height: baseImage.Height,
                  type: baseImage.Type,
                  associationType: baseImage.associationType,
                });

                return this.renderPreviewCarouselForRightRail({
                  previewImage,
                  cropSettings,
                  previewTexts,
                  hasCropSettingsChanged,
                  hasEffectSettingsChanged,
                  selectedImageType,
                });
              }}
              adCreatorScenario={this.props.adCreatorScenario}
              isThumbsUpDownEnabled={isThumbsUpDownEnabled}
              isTestEnv={isTestEnv}
              isLinkedInAd={this.props.isLinkedInAd}
              onManualCropImage={this.props.onManualCropImage}
              onManualCropLogo={this.props.onManualCropLogo}
              onRenderAllowedAspectRatioControl={this.props.onRenderAllowedAspectRatioControl}
              disabledPivotItemKeys={(this.props.isImproveMMA || this.props.isLinkedInAd) ? [PivotItemKey.Effect] : []}
              customizedDefaultSelectedPivotItemKey={(this.props.isImproveMMA || this.props.isLinkedInAd) ? PivotItemKey.Crop : null}
              isImageFittingEnabled={this.props.isImageFittingEnabled}
            /> :
            <EditImage
              isImageEditDisplayed={this.state.isImageEditDisplayed}
              image={this.state.editingImage}
              imageOption={this.state.imageOption}
              previewTexts={previewTexts}
              onSave={this.onSaveEditImage}
              onCancel={this.onCancelEditImage}
              i18n={i18n}
              perfMarker={perfMarker.createChild('imageEditor')}
              isForVNext={isForVNext}
              isImageAdExtensionMode={isImageAdExtensionMode}
              genericImage={genericImage}
              placeHolderImages={placeHolderImages}
              isMultimediaAd={this.props.isMultimediaAd}
              permissions={permissions}
              adPreviewConfig={this.props.adPreviewConfig}
              enableUnifiedAdPreview={this.props.enableUnifiedAdPreview}
            />
        }
        {this.state.isBackgroundChangeEnabled && this.state.copilotEditingImage &&
          <React.Suspense fallback={<div />}>
            <this.ChangeImageBackgroundDialogLazy
              editingImage={this.state.copilotEditingImage}
              isOpen={this.state.isBackgroundSceneDialogOpen}
              onClose={this.onCancelApplyBackgroundScene}
              onApply={this.onApplyBackgroundScene}
              aid={aid}
              cid={cid}
              i18n={i18n}
              serverContext={serverContext}
              permissions={permissions}
              //
              perfMarker={perfMarker}
            //
            // finalUrl={this.props.finalUrl}
            />
          </React.Suspense>
        }
      </div>
    );

    if (!_.isEmpty(imagesPortalContainer)) {
      return (
        <React.Fragment>
          {imageExperienceElement}
        </React.Fragment>
      );
    }

    return (
      isLogoEnabled && !isBulkEditMMA && !isBusinessLogo ?
        (
          <React.Fragment>
            <hr />
            <Panel defaultExpanded className="collapsible-panel">
              <Panel.Heading className="collapsible-heading">
                <Panel.Title toggle className="collapsible-title">
                  {i18n.getString('ImageExperience_Asset_Title')}
                </Panel.Title>
              </Panel.Heading>
              <Panel.Collapse className="collapsible-collapse">
                {imageExperienceElement}
              </Panel.Collapse>
            </Panel>
            <hr />
          </React.Fragment>
        ) : (
          <React.Fragment>
            {imageExperienceElement}
          </React.Fragment>
        )
    );
  }
}

ImageExperienceComponent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  longHeadline: PropTypes.string,
  shortHeadline: PropTypes.string,
  description: PropTypes.string,
  businessName: PropTypes.string,
  extensionHeadline: PropTypes.string,
  url: PropTypes.string,
  callToAction: PropTypes.string,
  onImageEditCancelled: PropTypes.func,
  currentSelectedImageId: PropTypes.any, // number or string
  onImageSelected: PropTypes.func,
  onImageRemoved: PropTypes.func,
  onImageListUpdated: PropTypes.func,
  onVideoListUpdated: PropTypes.func,
  onSaveThumbnailAsDraft: PropTypes.func,
  imageList: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceImage)),
    PropTypes.instanceOf(ImageExperienceImage),
  ])),
  videoList: PropTypes.arrayOf(PropTypes.instanceOf(ImageExperienceVideo)),
  customThumbnailRecord: CustomThumbnailRecordType,
  onImagePreviewRequested: PropTypes.func,
  onVideoPreviewRequested: PropTypes.func,
  onUpdateFinalUrlPrefix: PropTypes.func,
  onUpdateFinalUrl: PropTypes.func,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  isForVNext: PropTypes.bool,
  isImageAdExtensionMode: PropTypes.bool,
  isMultimediaAd: PropTypes.bool,
  isAudienceAd: PropTypes.bool,
  genericImage: PropTypes.string,
  currentSelectedLogoId: PropTypes.string,
  placeHolderImages: PropTypes.arrayOf(PropTypes.string),
  isStockPhotoEnabled: PropTypes.bool,
  isMultimediaAdinMSANEnabled: PropTypes.bool,
  isCombinedMsanAndMmaAspectRatios: PropTypes.bool,
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
    applicationName: PropTypes.string,
    oDataTokenType: PropTypes.string,
  }),
  isLazyLoadEnabled: PropTypes.bool,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func.isRequired,
  }),
  permissions: PropTypes.shape({
    IsStockPhotoFilterEnabled: PropTypes.bool,
    IsVideoAdEditorEnabled: PropTypes.bool,
    isBoost200x100ImageEnabled: PropTypes.bool,
  }),
  isVideo: PropTypes.bool,
  onVideoUploadStatusUpdate: PropTypes.func,
  cropImage: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Type: PropTypes.string.isRequired,
  }),
  suggestion: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.shape({
      fullUrl: PropTypes.string.isRequired,
      thumbnailUrl: PropTypes.string.isRequired,
    })),
    inProgress: PropTypes.bool,
    hasMore: PropTypes.bool,
  }),
  suggestionPoller: PropTypes.shape({
    getLandingPageSuggestions: PropTypes.func,
    getMoreLandingPageSuggestions: PropTypes.func,
  }),
  isAdCreatorEnabled: PropTypes.bool,
  isFinalUrlImageRetrievalEnabled: PropTypes.bool,
  mediaRepository: PropTypes.instanceOf(MediaRepository),
  finalUrl: PropTypes.string,
  finalUrlProtocol: PropTypes.string,
  isLogoEnabled: PropTypes.bool,
  isBusinessLogo: PropTypes.bool,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }),
  setAddNewImageCallback: PropTypes.func,
  setAddNewImagesCallback: PropTypes.func,
  setOpenAddImageCallback: PropTypes.func,
  adCreatorScenario: PropTypes.shape({
    scenarioName: PropTypes.string,
    scenarioValue: PropTypes.string,
  }),
  serverContext: PropTypes.shape({
    IsAADUser: PropTypes.bool,
  }),
  imageErrorMessages: PropTypes.arrayOf(PropTypes.string),
  onManualCropImage: PropTypes.func,
  onManualCropLogo: PropTypes.func,
  recommendation: PropTypes.objectOf(PropTypes.any),
  onUploadStart: PropTypes.func,
  onUploadComplete: PropTypes.func,
  readOnly: PropTypes.bool,
  preferencesService: PropTypes.shape({}),
  dateFormat: PropTypes.string,
  timeZone: PropTypes.number,
  isImageExtensionsEnhancementsEnabled: PropTypes.bool,
  onImageCropCancelled: PropTypes.func,
  onImageReplaceCancelled: PropTypes.func,
  replaceImage: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Type: PropTypes.string.isRequired,
  }),
  isRenderAddInImageList: PropTypes.bool,
  imagesPortalContainer: PropTypes.instanceOf(Element),
  logosPortalContainer: PropTypes.instanceOf(Element),
  customMaxImageCount: PropTypes.number,
  isImproveMMA: PropTypes.bool,
  isBulkEditMMA: PropTypes.bool,
  isMMAV2Enabled: PropTypes.bool,
  isMSANBoostEnabled: PropTypes.bool,
  isBoost200x100ImageEnabled: PropTypes.bool,
  isBoostNewAspectRatioSupportEnabled: PropTypes.bool,
  enableImageDeleting: PropTypes.bool,
  enableStockMultipleSelection: PropTypes.bool,
  enableInvestMultipleSelection: PropTypes.bool,
  enableWebsiteMultipleSelection: PropTypes.bool,
  showCopilotOptionForImage: PropTypes.bool,
  enableImageListEdit: PropTypes.bool,
  postAssetEventLog: PropTypes.func,
  adPreviewConfig: PropTypes.shape({
    adType: PropTypes.string,
    placementConfig: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  enableUnifiedAdPreview: PropTypes.bool,
  editingPlacementType: PropTypes.string,
  isStockImageEnabled: PropTypes.bool,
  isStoreAppProng: PropTypes.bool,
  isStoreAppSpotLight: PropTypes.bool,
  isStoreAppTransparentLogo: PropTypes.bool,
  isUnifiedAppCampaignWindowsStore: PropTypes.bool,
  isAudienceMMAWithLogoAndCTA: PropTypes.bool,
  assetLibraryEventHandler: PropTypes.shape({}),
  isLinkedInAd: PropTypes.bool,
  customPreviewTexts: PropTypes.shape({}),
  customOnEditImage: PropTypes.func,
  customOnSaveEditImage: PropTypes.func,
  onRenderAllowedAspectRatioControl: PropTypes.func,
  supportImageUpscale: PropTypes.bool,
  isImageFittingEnabled: PropTypes.bool,
  isPMaxAspectRatiosV2Enabled: PropTypes.bool,
  isAdPreviewRefreshEnabled: PropTypes.bool,
};

ImageExperienceComponent.defaultProps = {
  longHeadline: '',
  shortHeadline: '',
  description: '',
  businessName: '',
  extensionHeadline: '',
  url: 'https://www.bing.com',
  callToAction: '',
  finalUrl: '',
  finalUrlProtocol: '',
  currentSelectedImageId: null,
  currentSelectedLogoId: null,
  onImageEditCancelled: _.noop,
  onImageSelected: _.noop,
  onImageRemoved: _.noop,
  onImageListUpdated: _.noop,
  onVideoListUpdated: _.noop,
  onSaveThumbnailAsDraft: _.noop,
  onImagePreviewRequested: _.noop,
  onVideoPreviewRequested: _.noop,
  onUpdateFinalUrl: _.noop,
  onUpdateFinalUrlPrefix: _.noop,
  isForVNext: false,
  isImageAdExtensionMode: false,
  isMultimediaAd: false,
  isAudienceAd: false,
  genericImage: null,
  placeHolderImages: [],
  isMultimediaAdinMSANEnabled: false,
  isCombinedMsanAndMmaAspectRatios: false,
  isStockPhotoEnabled: false,
  oDataConfig: null,
  isLazyLoadEnabled: true,
  activityLogger: {
    signal: _.noop,
  },
  permissions: { dynamic: {} },
  imageList: [],
  videoList: [],
  customThumbnailRecord: {},
  isVideo: false,
  onVideoUploadStatusUpdate: _.noop,
  cropImage: null,
  replaceImage: null,
  suggestion: {},
  isAdCreatorEnabled: false,
  isFinalUrlImageRetrievalEnabled: false,
  mediaRepository: null,
  suggestionPoller: {
    getLandingPageSuggestions: _.noop,
    getMoreLandingPageSuggestions: _.noop,
  },
  isLogoEnabled: false,
  isBusinessLogo: false,
  instrumentation: {
    addScenario: _.noop,
  },
  setAddNewImageCallback: _.noop,
  setAddNewImagesCallback: _.noop,
  setOpenAddImageCallback: _.noop,
  adCreatorScenario: null,
  serverContext: {},
  imageErrorMessages: [],
  onManualCropImage: _.noop,
  onManualCropLogo: _.noop,
  recommendation: {},
  onUploadStart: null,
  onUploadComplete: null,
  readOnly: false,
  preferencesService: null,
  dateFormat: 'M/d/yyyy',
  timeZone: 71,
  isImageExtensionsEnhancementsEnabled: false,
  onImageCropCancelled: _.noop,
  onImageReplaceCancelled: _.noop,
  isRenderAddInImageList: false,
  imagesPortalContainer: null,
  logosPortalContainer: null,
  customMaxImageCount: null,
  isImproveMMA: false,
  isBulkEditMMA: false,
  isMMAV2Enabled: false,
  isMSANBoostEnabled: false,
  isBoost200x100ImageEnabled: false,
  isBoostNewAspectRatioSupportEnabled: false,
  enableImageDeleting: false,
  enableStockMultipleSelection: true,
  enableInvestMultipleSelection: true,
  enableWebsiteMultipleSelection: true,
  showCopilotOptionForImage: false,
  enableImageListEdit: true,
  postAssetEventLog: _.noop,
  adPreviewConfig: {},
  enableUnifiedAdPreview: false,
  editingPlacementType: '',
  isStockImageEnabled: true,
  isStoreAppProng: false,
  isStoreAppSpotLight: false,
  isStoreAppTransparentLogo: false,
  isUnifiedAppCampaignWindowsStore: false,
  isAudienceMMAWithLogoAndCTA: false,
  assetLibraryEventHandler: {},
  isLinkedInAd: false,
  customPreviewTexts: undefined,
  customOnEditImage: null,
  customOnSaveEditImage: null,
  onRenderAllowedAspectRatioControl: null,
  supportImageUpscale: false,
  isImageFittingEnabled: false,
  isPMaxAspectRatiosV2Enabled: false,
  isAdPreviewRefreshEnabled: false,
};

const ImageExperience =
  withServerContext(withInstrumentation(withPerfMarker(ImageExperienceComponent)));

export { ImageExperience };
